import { useMemo, useState, type FormEvent } from "react";
import type {
  EditorCommands,
  EditorExportFormat,
  EditorExportOptions,
  EditorExportScale,
  EditorExportScope,
} from "../editor/editor_commands";
import { downloadBlob } from "../export/download_blob";
import { Modal } from "./modal";

export interface ExportDialogProps {
  readonly boardName: string;
  readonly commands: EditorCommands;
  readonly hasSelection: boolean;
  readonly onClose: () => void;
}

/** Collects explicit flattened-export settings and writes one local download. */
export function ExportDialog({ boardName, commands, hasSelection, onClose }: ExportDialogProps) {
  const [format, setFormat] = useState<EditorExportFormat>("png");
  const [scope, setScope] = useState<EditorExportScope>(hasSelection ? "selection" : "all");
  const [includeBackground, setIncludeBackground] = useState(true);
  const [padding, setPadding] = useState(24);
  const [scale, setScale] = useState<EditorExportScale>(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options: EditorExportOptions = useMemo(
    () => ({ format, scope, includeBackground, padding, scale }),
    [format, includeBackground, padding, scale, scope],
  );
  const estimate = commands.estimate(options);

  /** Generates the flattened output only after a deliberate submit gesture. */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const output = await commands.export(options);
      downloadBlob(output, `${safeExportName(boardName)}.${format}`);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The export could not be created.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Export flattened image"
      titleId="export-title"
      descriptionId="export-description"
      onClose={onClose}
    >
      <form className="export-form" onSubmit={(event) => void handleSubmit(event)}>
        <p id="export-description" className="modal__lede">
          Exports are newly encoded and contain no original image metadata.
        </p>

        <fieldset>
          <legend>Content</legend>
          <label>
            <input
              type="radio"
              name="scope"
              value="all"
              checked={scope === "all"}
              onChange={() => setScope("all")}
            />
            All content
          </label>
          <label>
            <input
              type="radio"
              name="scope"
              value="selection"
              checked={scope === "selection"}
              disabled={!hasSelection}
              onChange={() => setScope("selection")}
            />
            Selection
          </label>
        </fieldset>

        <div className="export-form__row">
          <label>
            Format
            <select
              value={format}
              onChange={(event) => setFormat(parseExportFormat(event.currentTarget.value))}
            >
              <option value="png">PNG</option>
              <option value="svg">SVG</option>
            </select>
          </label>
          <label>
            Padding
            <input
              type="number"
              min="0"
              max="256"
              step="4"
              value={padding}
              onChange={(event) =>
                setPadding(normalizeExportPadding(event.currentTarget.valueAsNumber))
              }
            />
          </label>
          <label>
            PNG scale
            <select
              value={scale}
              disabled={format !== "png"}
              onChange={(event) => setScale(parseExportScale(event.currentTarget.value))}
            >
              <option value="1">1×</option>
              <option value="2">2×</option>
              <option value="3">3×</option>
            </select>
          </label>
        </div>

        <label className="check-control">
          <input
            type="checkbox"
            checked={includeBackground}
            onChange={(event) => setIncludeBackground(event.currentTarget.checked)}
          />
          Include canvas background
        </label>

        <p className="export-estimate" aria-live="polite">
          {estimate === null
            ? "Add or select content to export."
            : `Estimated output: ${estimate.width.toLocaleString()} × ${estimate.height.toLocaleString()} px`}
        </p>
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <footer className="modal__actions">
          <button type="button" className="secondary-action" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={busy || estimate === null}>
            {busy ? "Exporting…" : `Export ${format.toUpperCase()}`}
          </button>
        </footer>
      </form>
    </Modal>
  );
}

/** Narrows one controlled format value without an assertion. */
function parseExportFormat(value: string): EditorExportFormat {
  if (value === "png" || value === "svg") {
    return value;
  }
  throw new Error("The export format control returned an invalid value.");
}

/** Narrows one controlled scale value without an assertion. */
function parseExportScale(value: string): EditorExportScale {
  if (value === "1") {
    return 1;
  }
  if (value === "2") {
    return 2;
  }
  if (value === "3") {
    return 3;
  }
  throw new Error("The export scale control returned an invalid value.");
}

/** Keeps transient number-input values inside the bounded export contract. */
function normalizeExportPadding(value: number): number {
  return Number.isFinite(value) ? Math.min(256, Math.max(0, value)) : 0;
}

/** Removes path separators and blank names from a browser download filename. */
function safeExportName(boardName: string): string {
  const sanitized = boardName.replace(/[\\/:*?"<>|]/g, "_").trim();
  return sanitized.length > 0 ? sanitized : "Shotboard export";
}
